<template>
  <q-dialog v-model="localShow" maximized transition-show="slide-left" transition-hide="slide-right">
    <q-card class="bom-management-dialog">
      <!-- Header -->
      <q-toolbar class="bg-primary text-white">
        <q-toolbar-title>
          <q-icon name="inventory_2" class="q-mr-sm" />
          BOM Management - {{ bomData.quote_number }}
          <q-badge v-if="bomData.option_name" color="white" text-color="primary" class="q-ml-sm">
            {{ bomData.option_name }}
          </q-badge>
        </q-toolbar-title>
        <q-btn v-close-popup flat round dense icon="close" />
      </q-toolbar>

      <!-- Tabs -->
      <q-tabs v-model="activeTab" align="left" class="bg-grey-2">
        <q-tab name="unassigned" icon="warning" label="Unassigned Devices">
          <q-badge color="negative" floating>{{ unassignedCount }}</q-badge>
        </q-tab>
        <q-tab name="shortages" icon="priority_high" label="Shortages">
          <q-badge color="warning" floating>{{ shortagesCount }}</q-badge>
        </q-tab>
        <q-tab name="matched" icon="check_circle" label="Matched">
          <q-badge color="positive" floating>{{ matchedCount }}</q-badge>
        </q-tab>
        <q-tab name="excess" icon="add_circle" label="Excess">
          <q-badge color="orange" floating>{{ excessCount }}</q-badge>
        </q-tab>
        <q-tab name="by_others" icon="person_off" label="By Others">
          <q-badge color="grey-6" floating>{{ byOthersCount }}</q-badge>
        </q-tab>
      </q-tabs>

      <!-- Tab Panels -->
      <q-tab-panels v-model="activeTab" animated class="bom-panels">
        <!-- UNASSIGNED DEVICES TAB -->
        <q-tab-panel name="unassigned">
          <div v-if="groupedDevices.length === 0" class="text-center q-pa-xl text-grey-6">
            <q-icon name="check_circle" size="64px" color="positive" />
            <div class="text-h6 q-mt-md">All devices have parts assigned!</div>
          </div>

          <div v-else>
            <div v-for="group in groupedDevices" :key="group.key" class="device-group q-mb-lg">
              <!-- Group Header -->
              <q-card flat bordered>
                <q-card-section class="bg-grey-1">
                  <div class="row items-center">
                    <q-checkbox
                      :model-value="isGroupFullySelected(group)"
                      :indeterminate="isGroupPartiallySelected(group)"
                      @update:model-value="toggleGroupSelection(group, $event)"
                    />
                    <div class="col">
                      <div class="text-subtitle1 text-weight-medium">
                        {{ group.name }}
                        <q-badge color="primary" class="q-ml-sm">{{ group.count }}</q-badge>
                      </div>
                      <div class="text-caption text-grey-7">{{ group.brick_class }}</div>
                    </div>
                  </div>
                </q-card-section>

                <!-- Device List -->
                <q-card-section>
                  <q-list dense>
                    <q-item v-for="device in group.devices" :key="device.id">
                      <q-item-section side>
                        <q-checkbox
                          :model-value="isDeviceSelected(device.id)"
                          @update:model-value="setDeviceSelected(device.id, $event)"
                        />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label caption>In: {{ device.equipment_name }}</q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-card-section>

                <!-- Assignment Section (shown when devices selected) -->
                <q-card-section v-if="getSelectedCount(group) > 0" class="bg-blue-1">
                  <div class="text-caption text-weight-medium q-mb-sm">
                    Assign part to {{ getSelectedCount(group) }} selected device(s)
                  </div>
                  <div class="row q-gutter-sm">
                    <div class="col">
                      <q-select
                        :model-value="getGroupSelectedPart(group.key)"
                        :options="getGroupFilteredOptions(group.key)"
                        option-label="display_name"
                        label="Select Part to Assign"
                        outlined
                        dense
                        use-input
                        fill-input
                        hide-selected
                        input-debounce="300"
                        clearable
                        @update:model-value="(val) => setGroupSelectedPart(group.key, val)"
                        @filter="(val, update) => filterDeviceParts(val, update, group)"
                      >
                        <template #no-option>
                          <q-item>
                            <q-item-section class="text-grey">
                              Type to search all device parts...
                            </q-item-section>
                          </q-item>
                        </template>
                        <template #option="scope">
                          <q-item v-bind="scope.itemProps">
                            <q-item-section side v-if="isGoodMatch(scope.opt, group)">
                              <q-icon name="star" color="amber" size="xs" />
                            </q-item-section>
                            <q-item-section>
                              <q-item-label>
                                {{ scope.opt.part_number }}
                                <q-badge v-if="isTemplateMatch(scope.opt, group)" color="primary" class="q-ml-xs">
                                  Exact
                                </q-badge>
                                <q-badge v-else-if="isExactBrickMatch(scope.opt, group)" color="positive" class="q-ml-xs">
                                  Match
                                </q-badge>
                              </q-item-label>
                              <q-item-label caption>
                                {{ scope.opt.manufacturer }} {{ scope.opt.model }}
                              </q-item-label>
                            </q-item-section>
                          </q-item>
                        </template>

                        <template v-if="devicePartOptions.length > 0" #after-options>
                          <q-separator />
                          <q-item>
                            <q-item-section class="text-caption text-grey-7">
                              Showing {{ getGroupFilteredOptions(group.key).length }} part(s)
                            </q-item-section>
                          </q-item>
                        </template>
                      </q-select>
                    </div>
                    <div>
                      <q-btn
                        color="primary"
                        label="Assign"
                        icon="check"
                        :disable="!getGroupSelectedPart(group.key)"
                        :loading="assigningGroup === group.key"
                        @click="assignPartToSelected(group)"
                      />
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </div>
          </div>
        </q-tab-panel>

        <!-- SHORTAGES TAB -->
        <q-tab-panel name="shortages">
          <div v-if="bomData.shortages?.length === 0" class="text-center q-pa-xl text-grey-6">
            <q-icon name="check_circle" size="64px" color="positive" />
            <div class="text-h6 q-mt-md">No shortages - all parts are matched!</div>
          </div>

          <q-list v-else bordered separator>
            <q-item v-for="shortage in bomData.shortages" :key="shortage.part_number">
              <q-item-section>
                <q-item-label class="text-weight-medium">{{ shortage.part_number }}</q-item-label>
                <q-item-label caption>{{ shortage.manufacturer }} {{ shortage.model }}</q-item-label>
                <q-item-label caption class="text-warning q-mt-sm">
                  <q-icon name="priority_high" size="xs" />
                  Need: {{ shortage.needed_qty }} | Quoted: {{ shortage.quoted_qty }} |
                  <strong>Short: {{ shortage.shortage_qty }}</strong>
                </q-item-label>
              </q-item-section>

              <q-item-section side style="min-width: 500px">
                <div class="row q-gutter-sm items-end">
                  <div class="col">
                    <q-select
                      v-model="shortage.selectedProduct"
                      :options="shortage.productOptions || []"
                      option-label="name"
                      label="Select Product from Catalog"
                      outlined
                      dense
                      use-input
                      fill-input
                      hide-selected
                      input-debounce="300"
                      clearable
                      @filter="(val, update) => searchProducts(val, update, shortage)"
                      @popup-show="() => loadProductsForShortage(shortage)"
                      @focus="() => loadProductsForShortage(shortage)"
                    >
                      <template #option="scope">
                        <q-item v-bind="scope.itemProps">
                          <q-item-section>
                            <q-item-label>{{ scope.opt.name }}</q-item-label>
                            <q-item-label caption>
                              {{ scope.opt.part_number }} - ${{ scope.opt.unit_price }}
                            </q-item-label>
                          </q-item-section>
                        </q-item>
                      </template>

                      <template #no-option>
                        <q-item>
                          <q-item-section class="text-grey">
                            Type to search for products or wait for auto-load...
                          </q-item-section>
                        </q-item>
                      </template>
                    </q-select>
                  </div>
                  <div class="row q-gutter-xs">
                    <q-btn
                      color="primary"
                      :label="`Add ${shortage.shortage_qty} to Quote`"
                      icon="add"
                      :disable="!shortage.selectedProduct"
                      :loading="addingShortage === shortage.part_number"
                      @click="addShortageToQuote(shortage)"
                    />
                    <q-btn
                      color="grey-6"
                      label="By Others"
                      icon="person_off"
                      outline
                      :loading="markingByOthers === shortage.part_number"
                      @click="markShortageAsByOthers(shortage)"
                    >
                      <q-tooltip>Mark this part as supplied by others</q-tooltip>
                    </q-btn>
                  </div>
                </div>
              </q-item-section>
            </q-item>
          </q-list>
        </q-tab-panel>

        <!-- MATCHED TAB -->
        <q-tab-panel name="matched">
          <div v-if="bomData.matched?.length === 0" class="text-center q-pa-xl text-grey-6">
            <q-icon name="info" size="64px" />
            <div class="text-h6 q-mt-md">No matched parts yet</div>
          </div>

          <q-list v-else bordered separator>
            <q-item v-for="match in bomData.matched" :key="match.part_number">
              <q-item-section avatar>
                <q-icon name="check_circle" color="positive" size="md" />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-weight-medium">{{ match.part_number }}</q-item-label>
                <q-item-label caption>{{ match.manufacturer }} {{ match.model }}</q-item-label>
                <q-item-label caption class="text-positive q-mt-sm">
                  Quantity: {{ match.needed_qty }} (fully matched)
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-tab-panel>

        <!-- EXCESS TAB -->
        <q-tab-panel name="excess">
          <div v-if="bomData.excess?.length === 0" class="text-center q-pa-xl text-grey-6">
            <q-icon name="check_circle" size="64px" color="positive" />
            <div class="text-h6 q-mt-md">No excess parts in quote</div>
          </div>

          <q-list v-else bordered separator>
            <q-item v-for="excess in bomData.excess" :key="excess.part_number">
              <q-item-section>
                <q-item-label class="text-weight-medium">
                  {{ excess.quote_sources?.[0]?.product_name || excess.description || excess.part_number }}
                </q-item-label>
                <q-item-label caption class="text-grey-7">
                  Part: {{ excess.part_number }}
                  <span v-if="excess.manufacturer && excess.manufacturer !== 'Unknown'">
                    | {{ excess.manufacturer }} {{ excess.model }}
                  </span>
                </q-item-label>
                <q-item-label caption class="text-orange q-mt-xs">
                  <q-icon name="warning" size="xs" />
                  Quoted: {{ excess.quoted_qty }} (not needed by any allocated device)
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-btn
                  flat
                  round
                  dense
                  icon="delete"
                  color="negative"
                  @click="confirmRemoveExcess(excess)"
                >
                  <q-tooltip>Remove from quote</q-tooltip>
                </q-btn>
              </q-item-section>
            </q-item>
          </q-list>
        </q-tab-panel>

        <!-- BY OTHERS TAB -->
        <q-tab-panel name="by_others">
          <div v-if="!bomData.by_others || bomData.by_others.length === 0" class="text-center q-pa-xl text-grey-6">
            <q-icon name="check_circle" size="64px" color="positive" />
            <div class="text-h6 q-mt-md">No items excluded from scope</div>
            <p class="text-body2 q-mt-sm">
              All equipment and devices are included in this quote's supply scope.
            </p>
          </div>

          <div v-else>
            <q-banner class="bg-grey-2 q-mb-md" rounded>
              <template #avatar>
                <q-icon name="info" color="grey-7" />
              </template>
              <div class="text-body2">
                These items are marked as "By Others" based on supply exclusion rules.
                Click the include button to add them to this quote's scope.
              </div>
            </q-banner>

            <q-list bordered separator>
              <q-item v-for="item in bomData.by_others" :key="`by-others-${item.device_id || item.part_number}`">
                <q-item-section avatar>
                  <q-icon name="person_off" color="grey-6" />
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-weight-medium">
                    {{ item.device_name || item.part_name }}
                  </q-item-label>
                  <q-item-label caption>
                    {{ formatBrickClass(item.brick_class) }}
                  </q-item-label>
                  <q-item-label caption class="q-mt-xs">
                    <q-badge color="grey-6" outline>
                      {{ item.exclusion_reason }}
                    </q-badge>
                    <q-badge
                      :color="item.exclusion_source === 'global' ? 'blue' : (item.exclusion_source === 'tender' ? 'teal' : 'orange')"
                      outline
                      class="q-ml-xs"
                    >
                      {{ item.exclusion_source }}
                    </q-badge>
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-btn
                    color="positive"
                    label="Include in Scope"
                    icon="add_circle"
                    size="sm"
                    outline
                    :loading="includingItem === (item.device_id || item.part_number)"
                    @click="includeItemInScope(item)"
                  />
                </q-item-section>
              </q-item>
            </q-list>
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { api } from '@/services/api';
  import { formatBrickClass } from '@/composables/brick-schema/operations/useBrickValidation.js';

  const props = defineProps({
    modelValue: Boolean,
    quoteId: {
      type: Number,
      required: true,
    },
    bomData: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(['update:modelValue', 'refresh']);

  const $q = useQuasar();

  // Local state
  const localShow = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val),
  });

  const activeTab = ref('unassigned');
  const devicePartOptions = ref([]);
  const productOptions = ref([]);
  const assigningGroup = ref(null);
  const addingShortage = ref(null);
  const markingByOthers = ref(null);
  const includingItem = ref(null);
  const deviceSelections = ref({});
  const groupSelectedParts = ref({}); // Track selected part per group key
  const groupFilteredOptions = ref({}); // Track filtered options per group key

  // Initialize device part options from excess parts
  watch(() => props.bomData.excess, (excess) => {
    if (!excess || excess.length === 0) {
      devicePartOptions.value = [];
      return;
    }

    // Convert excess parts directly to dropdown options
    // Include device_part_id if the part exists in the registry
    devicePartOptions.value = excess.map((part) => ({
      id: part.device_part_id, // Use existing device part ID if available
      part_number: part.part_number,
      manufacturer: part.manufacturer || 'Unknown',
      model: part.model || '',
      description: part.description || '',
      brick_class: part.brick_class || '', // Include for device part creation if needed
      display_name: `${part.part_number} - ${part.manufacturer || 'Unknown'} ${part.model || ''}`.trim(),
      isExcess: true, // Flag to indicate this is from excess
      supplierPartData: part.quote_sources?.[0], // Store supplier part data for creation
    }));

  }, { immediate: true });

  // Counts
  const unassignedCount = computed(() => props.bomData.unassigned_devices?.length || 0);
  const shortagesCount = computed(() => props.bomData.shortages?.length || 0);
  const matchedCount = computed(() => props.bomData.matched?.length || 0);
  const excessCount = computed(() => props.bomData.excess?.length || 0);
  const byOthersCount = computed(() => props.bomData.by_others?.length || 0);

  // Group unassigned devices
  const groupedDevices = computed(() => {
    if (!props.bomData.unassigned_devices?.length) {return [];}

    const groups = {};

    props.bomData.unassigned_devices.forEach((device) => {
      const key = `${device.name}|||${device.brick_class}`;

      if (!groups[key]) {
        groups[key] = {
          key,
          name: device.name,
          brick_class: device.brick_class,
          device_template_id: device.device_template_id || null,
          count: 0,
          devices: [],
        };
      }

      groups[key].devices.push({
        id: device.id,
        equipment_name: device.equipment_name,
      });
      groups[key].count++;
    });

    return Object.values(groups);
  });

  // Getter/setter for group selected part (reactive)
  function getGroupSelectedPart(groupKey) {
    return groupSelectedParts.value[groupKey] || null;
  }

  function setGroupSelectedPart(groupKey, value) {
    groupSelectedParts.value[groupKey] = value;
  }

  // Getter/setter for group filtered options (reactive)
  function getGroupFilteredOptions(groupKey) {
    const options = groupFilteredOptions.value[groupKey] || devicePartOptions.value;
    // Find the group to get its brick_class for sorting
    const group = groupedDevices.value.find((g) => g.key === groupKey);
    if (group) {
      return sortPartsByRelevance(options, group);
    }
    return options;
  }

  function setGroupFilteredOptions(groupKey, options) {
    groupFilteredOptions.value[groupKey] = options;
  }

  /**
   * Sort parts by relevance to the device group
   * Prioritizes: 1. Same device template, 2. Exact brick_class match, 3. Name similarity
   */
  function sortPartsByRelevance(parts, group) {
    if (!parts || parts.length === 0) {return parts;}

    const groupBrickClass = (group.brick_class || '').toLowerCase().replace('brick:', '');
    const groupName = (group.name || '').toLowerCase();
    const groupTemplateId = group.device_template_id || null;

    return [...parts].sort((a, b) => {
      const scoreA = calculateMatchScore(a, groupBrickClass, groupName, groupTemplateId);
      const scoreB = calculateMatchScore(b, groupBrickClass, groupName, groupTemplateId);
      return scoreB - scoreA; // Higher score first
    });
  }

  /**
   * Calculate a match score for a part against the device group
   * Higher score = better match
   */
  function calculateMatchScore(part, groupBrickClass, groupName, groupTemplateId) {
    let score = 0;

    // Same device template (highest priority - this IS the product for this device)
    if (groupTemplateId && part.device_template_id && part.device_template_id === groupTemplateId) {
      score += 500;
    }

    const partBrickClass = (part.brick_class || '').toLowerCase().replace('brick:', '');
    const partNumber = (part.part_number || '').toLowerCase();
    const partManufacturer = (part.manufacturer || '').toLowerCase();
    const partModel = (part.model || '').toLowerCase();
    const partDescription = (part.description || '').toLowerCase();

    // Exact brick_class match
    if (partBrickClass && groupBrickClass && partBrickClass === groupBrickClass) {
      score += 100;
    }
    // Partial brick_class match (contains)
    else if (partBrickClass && groupBrickClass &&
      (partBrickClass.includes(groupBrickClass) || groupBrickClass.includes(partBrickClass))) {
      score += 50;
    }

    // Name appears in part number, model, or description
    if (groupName) {
      const nameWords = groupName.split(/[\s_-]+/).filter((w) => w.length > 2);

      for (const word of nameWords) {
        if (partNumber.includes(word)) {score += 30;}
        if (partModel.includes(word)) {score += 25;}
        if (partDescription.includes(word)) {score += 15;}
        if (partManufacturer.includes(word)) {score += 10;}
      }
    }

    return score;
  }

  /**
   * Check if a part is a good match (score > threshold)
   */
  function isGoodMatch(part, group) {
    const groupBrickClass = (group.brick_class || '').toLowerCase().replace('brick:', '');
    const groupName = (group.name || '').toLowerCase();
    const groupTemplateId = group.device_template_id || null;
    return calculateMatchScore(part, groupBrickClass, groupName, groupTemplateId) >= 30;
  }

  /**
   * Check if a part is a template match (same device template)
   */
  function isTemplateMatch(part, group) {
    return group.device_template_id && part.device_template_id && part.device_template_id === group.device_template_id;
  }

  /**
   * Check if a part has an exact brick_class match
   */
  function isExactBrickMatch(part, group) {
    const partBrickClass = (part.brick_class || '').toLowerCase().replace('brick:', '');
    const groupBrickClass = (group.brick_class || '').toLowerCase().replace('brick:', '');
    return partBrickClass && groupBrickClass && partBrickClass === groupBrickClass;
  }

  // Selection helpers
  function isDeviceSelected(deviceId) {
    return deviceSelections.value[deviceId] || false;
  }

  function setDeviceSelected(deviceId, value) {
    deviceSelections.value[deviceId] = value;
  }

  function isGroupFullySelected(group) {
    return group.devices.every((d) => isDeviceSelected(d.id));
  }

  function isGroupPartiallySelected(group) {
    const selectedCount = group.devices.filter((d) => isDeviceSelected(d.id)).length;
    return selectedCount > 0 && selectedCount < group.devices.length;
  }

  function toggleGroupSelection(group, value) {
    group.devices.forEach((d) => setDeviceSelected(d.id, value));
  }

  function getSelectedCount(group) {
    return group.devices.filter((d) => isDeviceSelected(d.id)).length;
  }

  // Filter device parts (combines excess parts with searchable device parts)
  async function filterDeviceParts(val, update, group) {

    // If empty search, show all excess parts
    if (!val || val.length < 2) {
      update(() => {
        setGroupFilteredOptions(group.key, devicePartOptions.value);
      });
      return;
    }

    // User is searching - query API for device parts
    try {
      const response = await api.get('/device-parts/search', { q: val });
      const data = response.data || response;
      const searchResults = (Array.isArray(data) ? data : []).map((part) => ({
        ...part,
        display_name: `${part.part_number} - ${part.manufacturer} ${part.model}`,
        isExcess: false,
      }));

      update(() => {
        // Combine excess parts (if they match) with search results
        const filteredExcess = devicePartOptions.value.filter((opt) =>
          opt.part_number.toLowerCase().includes(val.toLowerCase()) ||
          opt.manufacturer.toLowerCase().includes(val.toLowerCase()) ||
          opt.model.toLowerCase().includes(val.toLowerCase()),
        );

        const combined = [...filteredExcess, ...searchResults];
        setGroupFilteredOptions(group.key, combined);
      });
    } catch (error) {
      console.error('[BOM Filter] Failed to search device parts:', error);
      update(() => {
        setGroupFilteredOptions(group.key, devicePartOptions.value);
      });
    }
  }

  // Assign part to selected devices
  async function assignPartToSelected(group) {
    const selectedDevices = group.devices.filter((d) => isDeviceSelected(d.id));
    const selectedPart = getGroupSelectedPart(group.key);

    if (!selectedDevices.length || !selectedPart) {return;}

    assigningGroup.value = group.key;

    try {
      let devicePartId = selectedPart.id;

      // If this is an excess part without a device_part_id, find or create it
      if (selectedPart.isExcess && !devicePartId) {

        try {
          // Try to find existing device part first
          const searchResponse = await api.get('/device-parts/search', { q: selectedPart.part_number });
          const searchData = searchResponse.data || searchResponse;
          const existingPart = (Array.isArray(searchData) ? searchData : [])
            .find((p) => p.part_number === selectedPart.part_number);

          if (existingPart) {
            devicePartId = existingPart.id;
          } else {
            // Create new device part with all required fields
            const response = await api.post('/device-parts', {
              part_number: selectedPart.part_number,
              manufacturer: selectedPart.manufacturer !== 'Unknown' ? selectedPart.manufacturer : null,
              model: selectedPart.model || null,
              description: selectedPart.description || null,
              brick_class: selectedPart.brick_class || 'brick:Equipment', // Default brick class
            });

            const data = response.data || response;
            devicePartId = data.id;
          }
        } catch (searchError) {
          console.error('[BOM] Error finding/creating device part:', searchError);
          throw new Error('Failed to find or create device part: ' + (searchError.message || 'Unknown error'));
        }
      }

      // Now assign the device_part to all selected devices
      for (const device of selectedDevices) {
        await api.patch(`/quotes/${props.quoteId}/bom/devices/${device.id}/assign-part`, {
          device_part_id: devicePartId,
        });
      }

      $q.notify({
        type: 'positive',
        message: `Assigned ${selectedPart.part_number} to ${selectedDevices.length} device(s)`,
      });

      emit('refresh');

      // Reset selection
      group.devices.forEach((d) => setDeviceSelected(d.id, false));
      setGroupSelectedPart(group.key, null);

    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to assign part',
      });
    } finally {
      assigningGroup.value = null;
    }
  }

  // Auto-load products for a shortage when dropdown opens
  async function loadProductsForShortage(shortage) {
    if (shortage.productOptions && shortage.productOptions.length > 0) {
      return;
    }


    try {
      const response = await api.get('/products', { search: shortage.part_number });
      const data = response.data || response;

      shortage.productOptions = Array.isArray(data) ? data : [];

      if (shortage.productOptions.length > 0) {

        // Auto-select if exactly one match found
        if (shortage.productOptions.length === 1) {
          shortage.selectedProduct = shortage.productOptions[0];
        }
      }
    } catch (error) {
      console.error('[BOM Auto-load] Error loading products:', error);
      shortage.productOptions = [];
    }
  }

  // Search products
  async function searchProducts(val, update, shortage) {

    // If empty or too short, show the auto-loaded options (if any)
    if (!val || val.length < 2) {
      update(() => {
        // If products were already auto-loaded, they'll show
        // Otherwise, trigger auto-load
        if (!shortage.productOptions || shortage.productOptions.length === 0) {
          loadProductsForShortage(shortage);
        }
      });
      return;
    }

    try {
      const response = await api.get('/products', { search: val });

      const data = response.data || response;

      if (Array.isArray(data) && data.length > 0) {
      }

      update(() => {
        shortage.productOptions = Array.isArray(data) ? data : [];
      });
    } catch (error) {
      console.error('[BOM Product Search] Error:', error);
      console.error('[BOM Product Search] Error response:', error.response);
      update(() => {
        shortage.productOptions = [];
      });
    }
  }

  // Add shortage to quote
  async function addShortageToQuote(shortage) {
    if (!shortage.selectedProduct) {return;}

    addingShortage.value = shortage.part_number;

    try {
      await api.post(`/quotes/${props.quoteId}/items`, {
        product_id: shortage.selectedProduct.id,
        description: shortage.selectedProduct.description || '',
        quantity: shortage.shortage_qty,
        unit_price: shortage.selectedProduct.unit_price,
        type: 'product',
      });

      $q.notify({
        type: 'positive',
        message: `Added ${shortage.shortage_qty} × ${shortage.selectedProduct.name} to quote`,
      });

      emit('refresh');
      shortage.selectedProduct = null;

    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to add to quote',
      });
    } finally {
      addingShortage.value = null;
    }
  }

  // Remove excess part
  function confirmRemoveExcess(excess) {
    $q.dialog({
      title: 'Remove Excess Part',
      message: `Remove ${excess.part_number} (qty: ${excess.excess_qty}) from quote?`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        // Find the supplier part ID from quote sources
        const supplierPartId = excess.quote_sources?.find((s) => s.type === 'supplier_part')?.id;

        if (!supplierPartId) {
          throw new Error('Cannot remove - no supplier part found');
        }

        await api.delete(`/quotes/${props.quoteId}/bom/supplier-parts/${supplierPartId}`);

        $q.notify({
          type: 'positive',
          message: 'Excess part removed from quote',
        });

        emit('refresh');
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: error.message || 'Failed to remove part',
        });
      }
    });
  }

  /**
   * Mark a shortage as "By Others" by creating an exclusion rule
   */
  async function markShortageAsByOthers(shortage) {
    markingByOthers.value = shortage.part_number;
    try {
      // Create a quote-specific exclusion rule for this part
      const ruleData = {
        target_type: 'device_part',
        target_value: shortage.part_number,
        reason: 'By mechanical contractor',
        is_excluded: true,
      };

      await api.post(`/quotes/${props.quoteId}/supply-rules`, ruleData);

      $q.notify({
        type: 'positive',
        message: `"${shortage.part_number}" marked as By Others`,
      });

      emit('refresh');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to mark as by others: ' + error.message,
      });
    } finally {
      markingByOthers.value = null;
    }
  }

  /**
   * Include an excluded item back in scope by creating an override rule
   */
  async function includeItemInScope(item) {
    const itemKey = item.device_id || item.part_number;
    includingItem.value = itemKey;
    try {
      // Create a quote-specific override rule (is_excluded = false)
      const ruleData = {
        target_type: item.brick_class ? 'brick_class' : 'device_part',
        target_value: item.brick_class || item.part_number,
        reason: 'Included per quote requirement',
        is_excluded: false,
      };

      await api.post(`/quotes/${props.quoteId}/supply-rules`, ruleData);

      $q.notify({
        type: 'positive',
        message: `"${item.device_name || item.part_name}" included in scope`,
      });

      emit('refresh');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to include in scope: ' + error.message,
      });
    } finally {
      includingItem.value = null;
    }
  }

  // Auto-switch to first non-empty tab
  watch(() => props.modelValue, (newVal) => {
    if (newVal) {
      if (unassignedCount.value > 0) {activeTab.value = 'unassigned';}
      else if (shortagesCount.value > 0) {activeTab.value = 'shortages';}
      else if (matchedCount.value > 0) {activeTab.value = 'matched';}
      else if (excessCount.value > 0) {activeTab.value = 'excess';}
      else if (byOthersCount.value > 0) {activeTab.value = 'by_others';}
    }
  });
</script>

<style scoped>
.bom-management-dialog {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.bom-panels {
  flex: 1;
  overflow: auto;
}

.device-group {
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
